import { plainText, requiredValidator, richText } from '../../../../../modules/validators';
import { trimSpacesLeft } from '../../../../../modules/formatters';
import { FormVmItemConfigType, FormVmValueValidatorResultType, InitialData } from '../../../../../infrastructure/form-viewmodel-controller';
import { buildConfigList } from '../../../../../infrastructure/utils/form-config.utils';
import { ConnectionType } from '../../../../../api/connections.dao';

export enum ConfigurePassportDisplayFieldKey {
    PASSPORT_LABEL = 'passportLabel',
    PASSPORT_DESCRIPTION = 'passportDescription',
    CONNECT_BUTTON_LABEL = 'connectButtonLabel',
    CONNECT_DETAILS_ENABLED = 'connectionDetailsEnabled',
    SHOW_HIDE_BUTTON_ENABLED = 'showHideButtonEnabled',
    SHOW_DIAL_IN_INFO = 'showDialInInfo',
    HIDE_DIAL_IN_INFO = 'hideDialInInfo',
    CONNECTION_TYPE = 'connectionType'
}

export const CONFIGURE_PASSPORT_LABEL = {
    id: 'oe.connection.passportLabel',
    defaultMessage: 'Passport Label',
    description: 'Label text'
};

export const CONFIGURE_PASSPORT_DESCRIPTION = {
    id: 'oe.connection.passportDescription',
    defaultMessage: 'Passport Description',
    description: 'Label text'
};

export const PASSPORT_CONNECT_BUTTON = {
    id: 'oe.connection.connectButtonLabel',
    defaultMessage: '"Connect" Button Label',
    description: 'Label text'
};

export const CONNECT_DETAILS_ENABLED_LABEL_TEXT = {
    message: 'Connection Details are {prefix}',
    id: 'oe.connection.connectionDetailsEnabled',
    description: 'Label text',
};

export const SHOW_HIDE_BUTTON_ENABLED_LABEL_TEXT = {
    message: 'Show/Hide button is {prefix}',
    id: 'oe.connection.showHideButtonEnabled',
    description: 'Label text',
};

export const SHOW_DIAL_IN_INFO_LABEL_TEXT = {
    defaultMessage: 'Show Dial-In Info',
    id: 'oe.connection.showDialInInfo',
    description: 'Label text',
};

export const HIDE_DIAL_IN_INFO_LABEL_TEXT = {
    defaultMessage: 'Hide Dial-In Info',
    id: 'oe.connection.hideDialInInfo',
    description: 'Label text',
};

export const audioBridgeRequiredValidator = (
    value: string = '',
    formValues: InitialData
): FormVmValueValidatorResultType => {
    const isAudioBridge = formValues[ConfigurePassportDisplayFieldKey.CONNECTION_TYPE] === ConnectionType.AudioBridge;

    if (!isAudioBridge) {
        return { valid: true, validationMessage: null };
    }

    // [OEP-30761] - as we temporarily hide the 'showHideButton' - requiredValidator becomes mandatory.
    // The changes will be reverted after 'showHideButton' is back, and the validator will be based on the 'showHideButton' value.
    // const isShowHideButtonEnabled = !!formValues[ConfigurePassportDisplayFieldKey.SHOW_HIDE_BUTTON_ENABLED];
    // if (isShowHideButtonEnabled) {
    //     return requiredValidator(value);
    // }
    //
    // return { valid: true, validationMessage: null };

    return requiredValidator(value);
};

export const otherConnectionsRequiredValidator = (
    value: string = '',
    formValues: InitialData
): FormVmValueValidatorResultType => {
    const otherConnections = (PASSPORT_CONFIGURATION_OTHER_CONNECTIONS.includes(formValues[ConfigurePassportDisplayFieldKey.CONNECTION_TYPE]));
    if (!otherConnections) {
        return { valid: true, validationMessage: null };
    }
    return requiredValidator(value);
};

export const FORM_CONFIG = {
    [ConfigurePassportDisplayFieldKey.CONNECTION_TYPE]: {
        defaultValue: '',
    },
    [ConfigurePassportDisplayFieldKey.PASSPORT_LABEL]: {
        labelDescriptor: CONFIGURE_PASSPORT_LABEL,
        defaultValue: '',
        validators: [plainText.maxLength.short],
        formatters: [trimSpacesLeft]
    },
    [ConfigurePassportDisplayFieldKey.PASSPORT_DESCRIPTION]: {
        labelDescriptor: CONFIGURE_PASSPORT_DESCRIPTION,
        defaultValue: '',
        validators: [richText.maxLength.long],
    },
    [ConfigurePassportDisplayFieldKey.CONNECT_BUTTON_LABEL]: {
        labelDescriptor: PASSPORT_CONNECT_BUTTON,
        defaultValue: 'Connect to Meeting',
        validators: [otherConnectionsRequiredValidator, plainText.maxLength.short],
        formatters: [trimSpacesLeft]
    },
    [ConfigurePassportDisplayFieldKey.CONNECT_DETAILS_ENABLED]: {
        defaultValue: true
    },
    [ConfigurePassportDisplayFieldKey.SHOW_HIDE_BUTTON_ENABLED]: {
        defaultValue: true
    },
    [ConfigurePassportDisplayFieldKey.SHOW_DIAL_IN_INFO]: {
        labelDescriptor: SHOW_DIAL_IN_INFO_LABEL_TEXT,
        defaultValue: 'Show Dial-In Info',
        validators: [audioBridgeRequiredValidator, plainText.maxLength.short],
        formatters: [trimSpacesLeft]
    },
    [ConfigurePassportDisplayFieldKey.HIDE_DIAL_IN_INFO]: {
        labelDescriptor: HIDE_DIAL_IN_INFO_LABEL_TEXT,
        defaultValue: 'Hide Dial-In Info',
        validators: [audioBridgeRequiredValidator, plainText.maxLength.short],
        formatters: [trimSpacesLeft]
    },
};

export const PASSPORT_CONFIGURATION_OTHER_CONNECTIONS = [
    ConnectionType.Zoom,
    ConnectionType.Webinar,
    ConnectionType.Bluejeans,
    ConnectionType.Other,
    ConnectionType.Teams,
    ConnectionType.Webex,
];

export const PASSPORT_CONFIGURATION_CONNECTIONS = [
    ...PASSPORT_CONFIGURATION_OTHER_CONNECTIONS,
    ConnectionType.AudioBridge,
    ConnectionType.Knovio
];

export const CONNECTION_FIELDS = {
    audioBridge: [
        ConfigurePassportDisplayFieldKey.PASSPORT_LABEL,
        ConfigurePassportDisplayFieldKey.PASSPORT_DESCRIPTION,
        ConfigurePassportDisplayFieldKey.SHOW_HIDE_BUTTON_ENABLED,
        ConfigurePassportDisplayFieldKey.SHOW_DIAL_IN_INFO,
        ConfigurePassportDisplayFieldKey.HIDE_DIAL_IN_INFO,
    ],
    knovio: [
        ConfigurePassportDisplayFieldKey.PASSPORT_LABEL,
        ConfigurePassportDisplayFieldKey.PASSPORT_DESCRIPTION,
    ],
    others: [
        ConfigurePassportDisplayFieldKey.PASSPORT_LABEL,
        ConfigurePassportDisplayFieldKey.PASSPORT_DESCRIPTION,
        ConfigurePassportDisplayFieldKey.CONNECT_BUTTON_LABEL,
        ConfigurePassportDisplayFieldKey.CONNECT_DETAILS_ENABLED
    ]
};

export enum PassportConfigurationMode {
    ConnectionTemplate = 'ConnectionTemplate',
    MeetingConnection = 'MeetingConnection',
}

export const configList: FormVmItemConfigType[] = buildConfigList(FORM_CONFIG);
